const OFFLINE_MEDIA_CACHE = 'app-cache-v1-videos';
const OFFLINE_IMAGE_CACHE = 'app-cache-v1-images';
const OFFLINE_MEDIA_PROMPT_KEY = 'offline-media-prompt-v1';
const OFFLINE_MEDIA_INSTALL_FLAG = 'offline-media-install-event-v1';
const VIDEO_FILE_PATTERN = /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i;
const IMAGE_FILE_PATTERN = /\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i;

const isStandaloneMode = () => {
  if (window.matchMedia?.('(display-mode: standalone)').matches) {
    return true;
  }

  return window.navigator.standalone === true;
};

const normalizeToAbsoluteUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return null;
  }
};

const getStrapiBaseUrl = () => {
  return window?.AppConfig?.strapiUrl || 'https://cms.brochure.urencoisotopes.com';
};

const normalizeImageToCacheKey = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const baseUrl = getStrapiBaseUrl();

  // Skip normalization for local assets (images, fonts, etc.)
  if (value.startsWith('/images/') || value.startsWith('/fonts/')) {
    return value;
  }

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  return baseUrl + (value.startsWith('/') ? value : `/${value}`);
};

const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const hasFilename = /\/[^/]+\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(url);
  if (!hasFilename) {
    return false;
  }

  const hasEmptyFilename = /\/\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i.test(url);
  if (hasEmptyFilename) {
    return false;
  }

  return true;
};

const isVideoUrl = (value) => {
  if (typeof value !== 'string') {
    return false;
  }

  if (value.includes('fast.wistia.net/embed/iframe/')) {
    return false;
  }

  return value.includes('/videos/') || VIDEO_FILE_PATTERN.test(value);
};

const isImageUrl = (value) => {
  if (typeof value !== 'string') {
    return false;
  }

  if (value.startsWith('data:')) {
    return false;
  }

  return value.includes('/uploads/');
};

const collectVideoUrlsFromValue = (value, found) => {
  if (!value) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectVideoUrlsFromValue(item, found));
    return;
  }

  if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectVideoUrlsFromValue(item, found));
    return;
  }

  if (typeof value === 'string' && isVideoUrl(value)) {
    const absoluteUrl = normalizeToAbsoluteUrl(value);
    if (absoluteUrl) {
      found.add(absoluteUrl);
    }
  }
};

export const extractVideoUrls = (pages = []) => {
  const found = new Set();
  collectVideoUrlsFromValue(pages, found);
  return [...found];
};

const collectImageUrlsFromValue = (value, found) => {
  if (!value) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectImageUrlsFromValue(item, found));
    return;
  }

  if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectImageUrlsFromValue(item, found));
    return;
  }

  if (typeof value === 'string' && isImageUrl(value)) {
    const cacheKey = normalizeImageToCacheKey(value);
    if (cacheKey && isValidImageUrl(cacheKey)) {
      found.add(cacheKey);
    }
  }
};

export const extractImageUrls = (pages = []) => {
  const found = new Set();
  collectImageUrlsFromValue(pages, found);
  return [...found];
};

const markPromptHandled = (result) => {
  try {
    localStorage.setItem(OFFLINE_MEDIA_PROMPT_KEY, result || 'dismissed');
  } catch {
    // ignore storage write failures
  }
};

const getPromptState = () => {
  try {
    return localStorage.getItem(OFFLINE_MEDIA_PROMPT_KEY);
  } catch {
    return null;
  }
};

const getInstallFlag = () => {
  try {
    return localStorage.getItem(OFFLINE_MEDIA_INSTALL_FLAG) === '1';
  } catch {
    return false;
  }
};

const clearInstallFlag = () => {
  try {
    localStorage.removeItem(OFFLINE_MEDIA_INSTALL_FLAG);
  } catch {
    // ignore storage write failures
  }
};

export const markInstalledEventCaptured = () => {
  try {
    localStorage.setItem(OFFLINE_MEDIA_INSTALL_FLAG, '1');
  } catch {
    // ignore storage write failures
  }
};

export const shouldPromptForOfflineMedia = () => {
  if (!isStandaloneMode()) {
    return false;
  }

  const promptState = getPromptState();
  const installFlag = getInstallFlag();

  if (installFlag && !promptState) {
    return true;
  }

  return !promptState;
};

export const dismissOfflineMediaPrompt = () => {
  markPromptHandled('dismissed');
  clearInstallFlag();
};

export const cacheOfflineMedia = async (urls = [], onProgress) => {
  if (!('caches' in window)) {
    throw new Error('Cache API is not available in this browser.');
  }

  const uniqueUrls = [...new Set(urls)].filter(Boolean);
  const total = uniqueUrls.length;

  if (total === 0) {
    markPromptHandled('empty');
    clearInstallFlag();
    return {
      total: 0,
      successCount: 0,
      failed: [],
    };
  }

  const cache = await caches.open(OFFLINE_MEDIA_CACHE);
  const failed = [];
  let successCount = 0;

  for (let i = 0; i < uniqueUrls.length; i += 1) {
    const url = uniqueUrls[i];

    try {
      const response = await fetch(url, { credentials: 'same-origin' });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      await cache.put(url, response.clone());
      successCount += 1;
    } catch (error) {
      failed.push({
        url,
        reason: error?.message || 'unknown error',
      });
    }

    if (typeof onProgress === 'function') {
      onProgress({
        total,
        completed: i + 1,
        successCount,
        failedCount: failed.length,
        currentUrl: url,
      });
    }
  }

  markPromptHandled(successCount > 0 ? 'downloaded' : 'failed');
  clearInstallFlag();

  return {
    total,
    successCount,
    failed,
  };
};

export const clearOfflineMediaCache = async () => {
  if (!('caches' in window)) {
    return false;
  }

  markPromptHandled('cleared');
  clearInstallFlag();
  return caches.delete(OFFLINE_MEDIA_CACHE);
};

export const getOfflineMediaCacheName = () => OFFLINE_MEDIA_CACHE;

export const cacheOfflineImages = async (urls = [], onProgress) => {
  if (!('caches' in window)) {
    throw new Error('Cache API is not available in this browser.');
  }

  const allowNoCorsFallback = import.meta.env.DEV;

  const uniqueUrls = [...new Set(urls)].filter(Boolean);
  const total = uniqueUrls.length;

  if (total === 0) {
    return {
      total: 0,
      successCount: 0,
      failed: [],
    };
  }

  const cache = await caches.open(OFFLINE_IMAGE_CACHE);
  const failed = [];
  let successCount = 0;

  for (let i = 0; i < uniqueUrls.length; i += 1) {
    const url = uniqueUrls[i];

    try {
      const response = await fetch(url, { credentials: 'same-origin' });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      await cache.put(url, response.clone());
      successCount += 1;
    } catch (error) {
      failed.push({
        url,
        reason: error?.message || 'unknown error',
      });
    }

    if (typeof onProgress === 'function') {
      onProgress({
        total,
        completed: i + 1,
        successCount,
        failedCount: failed.length,
        currentUrl: url,
      });
    }
  }

  return {
    total,
    successCount,
    failed,
  };
};
